/* Home Insurance Policy Cancellation */

import { unwrapResult } from "@reduxjs/toolkit";
import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { createCaseComplaint } from "../../../../redux/features/crm/caseComplaintSlice";
import { resetPolicyCancellationFlow } from "../../../../redux/features/motorInsurancePolicyCancellationFlowSlice";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
  policyView,
} from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW,
} from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";
import HomeInsuranceCancellationReasonPage from "./homeInsuranceCancellationReasonPage";
import HomeInsuranceCancellationReviewPage from "./homeInsuranceCancellationReviewPage";
import HomeInsuranceConfirmCancellationPage from "./homeInsuranceConfirmCancellationPage";

const CancelPolicyHomeInsurance = () => {
  const { currentStep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { policy_id } = useParams();

  const [selectedReasonOut, setSelectedReasonOut] = useState("");

  // Decrypt policy number from URL (same as policy details page)
  const secretKey = "i-hate-meej";

  // Revert to Base64 format using split and join
  const base64EncryptedValue = (policy_id || "")
    .split("-")
    .join("+") // Replace '-' with '+'
    .split("_")
    .join("/"); // Replace '_' with '/'

  // Decrypt the value
  let policyNumber = "";
  try {
    if (base64EncryptedValue) {
      const bytes = CryptoJS.AES.decrypt(base64EncryptedValue, secretKey);
      policyNumber = bytes.toString(CryptoJS.enc.Utf8);
    }
  } catch (e) { policyNumber = ""; }

  useEffect(() => {
    dispatch(resetPolicyCancellationFlow());

    // Load home insurance policy data if not already loaded
    const polReq = {
      PolicyNumber: policyNumber,
      Channel: getClientSource(),
    };
    dispatch(policyView(polReq));
  }, [dispatch, policyNumber]);

  const handleNavigationToPolicyOverview = () => {
    // We can use the original policy_id since it's already encrypted
    navigate(
      `/home-insurance/policy-overview/${policy_id}?tabType=ManagePolicy`
    );
  };

  // Get policy detail from Redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);
  const profileInfo = useSelector(selectProfileData);

  // Debug: Log all available data
  console.log("=== HOME INSURANCE POLICY DATA DEBUG ===");
  console.log("policyDetailsInfo:", policyDetailsInfo);
  console.log("policyDetailsPartyInfo:", policyDetailsPartyInfo);
  console.log("profileInfo:", profileInfo);
  console.log("policyNumber from URL:", policyNumber);
  console.log("=== END DEBUG ===");

  const confirmedCancellation = async () => {
    const payload = {
      FirstName:
        policyDetailsPartyInfo?.FirstName || profileInfo?.FirstName || "",
      LastName: policyDetailsPartyInfo?.LastName || profileInfo?.LastName || "",
      PhoneNumber:
        policyDetailsPartyInfo?.PhoneNumber || profileInfo?.PhoneNumber || "",
      Email: profileInfo?.Email || "",
      Subject: "Cancel Policy",
      Product: "Home Protect", // Home Insurance
      Lob: "PROPERTY", // Home Insurance → PROPERTY
      Descreption: `Policy to be cancelled. \nSelected Reason: ${selectedReasonOut}`,
      ClaimNumber: "",
      PolicyNumber: policyNumber,
      VNumber: "", // Empty string for home insurance (not motor)
      Channel: getClientSource(),
    };

    console.log("=== HOME INSURANCE CANCEL API PAYLOAD ===");
    console.log(JSON.stringify(payload, null, 2));
    console.log("=== END PAYLOAD ===");

    // Calling API case/complaint
    try {
      const caseComplaintAction = await dispatch(createCaseComplaint(payload));
      const caseComplaintResult = unwrapResult(caseComplaintAction);

      if (caseComplaintResult) {
        handleNavigationToPolicyOverview();
        toast.success(
          translate(
            "Your request for cancellation has been successfully sent",
            "تم إرسال طلب الإلغاء بنجاح"
          )
        );
      }
    } catch (error) {
      toast.error(
        translate(
          "Error in sending cancel policy request",
          "خطأ في إرسال طلب إلغاء السياسة"
        )
      );
    }
  };

  // Render page based on current step
  const renderPage = () => {
    switch (currentStep?.name) {
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW:
        return (
          <HomeInsuranceCancellationReviewPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON:
        return (
          <HomeInsuranceCancellationReasonPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            setSelectedReasonOut={setSelectedReasonOut}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION:
        return (
          <HomeInsuranceConfirmCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            confirmedCancellation={confirmedCancellation}
            selectedReasonOut={selectedReasonOut}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[7%] ipad:h-[93vh] ipad:overflow-y-scroll">
      {renderPage()}
    </div>
  );
};

export default CancelPolicyHomeInsurance;
